import { useEffect } from 'react';
import type { CommandeListee } from '../api/types';
import { formatCourt } from '../format';

/**
 * Confirmation avant de clôturer une commande.
 *
 * Clôturer n'est pas une action due parmi d'autres : c'est déclarer une fin, et
 * la commande quitte le travail courant pour l'archive. Le geste est
 * réversible — « Remettre en cours » existe — mais il ne doit pas se produire
 * au bout d'un clic distrait, surtout depuis une liste dense.
 *
 * La fenêtre dit ce qu'elle sait du transporteur : quand il a lui-même annoncé
 * la livraison, confirmer ne fait qu'entériner ; quand il ne dit rien, c'est une
 * affirmation de l'utilisateur, et elle mérite d'être posée comme telle.
 */
export function ConfirmerLivraison({
  commande, enAttente, onConfirmer, onFermer,
}: {
  commande: CommandeListee;
  enAttente: boolean;
  onConfirmer: () => void;
  onFermer: () => void;
}) {
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  const estimee = commande.calcul.livraisonEstimee;

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div
        className="box" role="dialog" aria-modal="true"
        aria-label={`Confirmer la livraison de ${commande.ref}`}
        style={{ borderTopColor: 'var(--vert)' }}
      >
        <div className="mhead">
          <h2>Marquer {commande.ref} livrée ?</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <p style={{ marginBottom: 14 }}>
            La commande quittera le travail courant pour l&apos;archive, et son suivi cessera.
          </p>

          <dl className="kv" style={{ marginBottom: 14 }}>
            <dt>Client</dt><dd style={{ textAlign: 'left' }}>{commande.client}</dd>
            <dt>Dernier statut</dt>
            <dd style={{ textAlign: 'left' }}>
              {commande.statutTransporteur || 'aucun point de suivi'}
            </dd>
            <dt>Livraison estimée</dt>
            <dd>{estimee ? formatCourt(estimee.date) : '—'}</dd>
          </dl>

          {commande.statutTransporteur ? (
            <p className="indice">
              Le transporteur a renvoyé ce statut le{' '}
              {formatCourt(commande.dateStatutTransporteur)}. Confirmer ne fait que l&apos;entériner.
            </p>
          ) : (
            <p className="err">
              Le transporteur n&apos;a rien renvoyé : cette livraison n&apos;est constatée que par
              vous. Le statut de suivi portera « Livré (saisie manuelle) ».
            </p>
          )}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 12 }}>
            Réversible : « Remettre en cours », en bas de cette fiche, restaure le suivi tel
            qu&apos;il était.
          </p>
        </div>

        <div className="mfoot">
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button
            type="button" className="btn pri" disabled={enAttente} onClick={onConfirmer}
          >
            {enAttente ? <><span className="spin" /> Enregistrement…</> : 'Confirmer la livraison'}
          </button>
        </div>
      </div>
    </div>
  );
}
